import type * as Sentry from "@sentry/nextjs";

type SentryInitOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;

/**
 * Options shared by every Sentry.init call (client, server, edge).
 *
 * @sentry/nextjs v11 changed two defaults that matter for us:
 *
 * 1. Tracing: v11 streams spans by default (`traceLifecycle: 'stream'`). GlitchTip only
 *    ingests the classic transaction format, so we pin the v10 behaviour.
 *    Docs: https://docs.sentry.io/platforms/javascript/guides/nextjs/migration/v10-to-v11/#tracing
 *    Option reference: https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#traceLifecycle
 *
 * 2. Data collection: `sendDefaultPii` was replaced by `dataCollection`, and the v11
 *    defaults collect user info / IP, cookies and request/response bodies. Owner decision:
 *    STRICT, i.e. nothing beyond what v10 sent with `sendDefaultPii: false` (and stricter
 *    where v10 parity is not needed).
 *    Docs: https://docs.sentry.io/platforms/javascript/guides/nextjs/migration/v10-to-v11/#data-collection
 *    Option reference: https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#dataCollection
 */
const V10_PII_HEADER_DENYLIST = ["forwarded", "-ip", "remote-", "via", "-user"];

// Vercel adds internal headers (e.g. x-vercel-proxy-signature, x-vercel-sc-headers) to every
// request; they now reach events through onRequestError, so they are filtered too.
const REQUEST_HEADER_DENYLIST = [...V10_PII_HEADER_DENYLIST, "x-vercel-"];

export const sharedSentryOptions = {
  traceLifecycle: "static",

  dataCollection: {
    // No automatic user.* fields. In v11 this also controls IP inference
    // ("User IP address inference is now controlled by dataCollection.userInfo").
    userInfo: false,
    // No cookies (v10 with sendDefaultPii off did not collect them).
    cookies: false,
    // Headers minus IP/forwarding/user headers: the v10 sendDefaultPii-off scrubbing,
    // as given in the migration guide's "keep v10 defaults" example.
    httpHeaders: {
      request: { deny: REQUEST_HEADER_DENYLIST },
      response: { deny: V10_PII_HEADER_DENYLIST },
    },
    // No request/response bodies in any direction.
    httpBodies: [],
    // Query params stay on, same as v10 (the SDK still filters sensitive keys).
    urlQueryParams: true,
    // Integrations we don't use; switched off so nothing appears if one gets added later.
    genAI: { inputs: false, outputs: false },
    graphQL: { document: false, variables: false },
    databaseQueryData: false,
    queues: false,
    // Stricter than v10: no local variable values from stack frames.
    stackFrameVariables: false,
  },
} satisfies Partial<SentryInitOptions>;
